<template>
  <div>
    <FadeTransition>
      <NavBar v-if="showNavbar">
        <template #left>
          <BackButton @click="backStep" />
        </template>
      </NavBar>
    </FadeTransition>
    <FadeTransition>
      <component :is="step.component" />
    </FadeTransition>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, provide } from 'vue';
import { useRoute } from 'vue-router/composables';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import { NavBar } from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import { FadeTransition } from '@/modules/common/components/FadeTransition';

import {
  getPortfolioSheetData,
  createPortfolio,
  IPortfolioSheetData
} from '@/modules/catalog/services/variable-income';

import {
  VariableIncomeAcquisitionComponents,
  EVariableIncomeAcquisitionSteps
} from './helpers';

const route = useRoute();

const currentStep = ref(EVariableIncomeAcquisitionSteps.TEMPLATE);
const portfolioSheetData = ref<IPortfolioSheetData>();
const amountFormatted = ref('');
const amountValue = ref(0);
const conclusionData = ref({
  portfolioId: '',
  portfolioName: '',
  expectedInvestmentDate: ''
});
const requestTerms = ref('');

const isLoading = ref(false);
const hasError = ref(false);
const isCreatingPortfolio = ref(false);

const templateId = computed(() => {
  return route.params.id as string;
});

const step = computed(() => {
  return {
    component: VariableIncomeAcquisitionComponents[currentStep.value]
  };
});

const showNavbar = computed(() => {
  return (
    currentStep.value !== EVariableIncomeAcquisitionSteps.TEMPLATE &&
    currentStep.value !== EVariableIncomeAcquisitionSteps.CONCLUSION
  );
});

function backStep() {
  currentStep.value = currentStep.value - 1;
}

function changeStep(step: EVariableIncomeAcquisitionSteps) {
  currentStep.value = step;
}

function changeAmountValue(value: number, formatted: string) {
  amountValue.value = value;
  amountFormatted.value = formatted;

  currentStep.value = EVariableIncomeAcquisitionSteps.PREVIEW;
}

function acceptTerms(request: string) {
  requestTerms.value = request;

  currentStep.value = EVariableIncomeAcquisitionSteps.INVESTMENT;
}

async function fetchPortfolioSheetData() {
  try {
    isLoading.value = true;
    hasError.value = false;

    portfolioSheetData.value = await getPortfolioSheetData(templateId.value);
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function fetchCreatePortfolio() {
  try {
    currentStep.value = EVariableIncomeAcquisitionSteps.LOADING;
    isCreatingPortfolio.value = true;

    conclusionData.value = await createPortfolio(
      templateId.value,
      requestTerms.value,
      amountValue.value
    );
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Não foi possível criar a carteira. Aguarde alguns instantes e tente novamente.',
      type: NotificationTypes.error
    });
    currentStep.value = EVariableIncomeAcquisitionSteps.INVESTMENT;
  } finally {
    isCreatingPortfolio.value = false;
  }
}

onMounted(() => {
  fetchPortfolioSheetData();
});

provide('isLoading', isLoading);
provide('hasError', hasError);
provide('portfolioSheetData', portfolioSheetData);
provide('isCreatingPortfolio', isCreatingPortfolio);
provide('amountFormatted', amountFormatted);
provide('amountValue', amountValue);
provide('currentStep', currentStep);
provide('conclusionData', conclusionData);
provide('templateId', templateId);

provide('changeStep', changeStep);
provide('fetchPortfolioSheetData', fetchPortfolioSheetData);
provide('fetchCreatePortfolio', fetchCreatePortfolio);
provide('changeAmountValue', changeAmountValue);
provide('acceptTerms', acceptTerms);
</script>

<style lang="less" scoped>
.navbar-container {
  border-bottom: none;
}
</style>
